import { CurrencyPipe, DecimalPipe, PercentPipe } from "@angular/common";
import { Injectable } from "@angular/core";
import { EnumValueLabelPipe, FeeType, HorizontalListData, HorizontalListFactory, PaymentFrequency, PaymentFrequencyLabel, ProductCode } from "common";
import { AccountWithRenewalData } from "../account.model";

@Injectable()
export class AccountEconomicsAdapter {
  constructor(
    private readonly currencyPipe: CurrencyPipe,
    private readonly decimalPipe: DecimalPipe,
    private readonly percentPipe: PercentPipe,
    private readonly enumValueLabelPipe: EnumValueLabelPipe,
  ) { }

  adaptOfferListData = (account: AccountWithRenewalData): HorizontalListData[] => {
    const isTermLoan = account?.productCode === ProductCode.Term;
    const isLCF = account?.productCode === ProductCode.Law;
    const offer = account?.offer;

    return [
      HorizontalListFactory.createKeyValueStringItem({
        title: isTermLoan ? 'Term amount' : 'Credit limit',
        displayedValue: this.formatCurrency(offer?.amount),
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'MSA credit limit',
        displayedValue: this.formatCurrency(account?.loanInfo?.msaCreditLimit),
        isItemHidden: !isLCF
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Annual interest rate',
        displayedValue: this.formatPercent(offer?.interestRate),
        isItemHidden: isTermLoan
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Factor rate',
        displayedValue: this.decimalPipe.transform(offer?.interestRate / 100 + 1, '1.4-4') || '-',
        isItemHidden: !isTermLoan
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Repayment term',
        displayedValue: this.formatTerm(offer?.repaymentTerm),
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Draw term',
        displayedValue: this.formatTerm(offer?.drawTerm),
        isItemHidden: isTermLoan
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'No payment terms',
        displayedValue: this.formatPaymentTerm(offer?.paymentFrequency, offer?.noPaymentTerms),
        isValueHidden: !offer?.noPaymentTerms
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Interest only payment terms',
        displayedValue: this.formatPaymentTerm(offer?.paymentFrequency, offer?.interestOnlyPaymentTerms),
        isValueHidden: !offer?.interestOnlyPaymentTerms
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Payment frequency',
        displayedValue: this.enumValueLabelPipe.transform(offer?.paymentFrequency, PaymentFrequency, PaymentFrequencyLabel),
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Daily interest rate',
        displayedValue: this.formatPercent(offer?.dailyInterestRate, 4),
        isItemHidden: isTermLoan
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Weekly interest rate',
        displayedValue: this.formatPercent(offer?.weeklyInterestRate, 4),
        isItemHidden: isTermLoan
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Simple interest rate',
        displayedValue: this.formatPercent(offer?.simpleInterestRate, 4),
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Draw fee',
        displayedValue: this.formatFee(offer?.drawDownFee, offer?.drawDownFeeType),
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Origination fee',
        displayedValue: this.formatFee(offer?.originationFee, offer?.originationFeeType),
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Renewal fee',
        displayedValue: this.formatFee(offer?.renewalFee, offer?.renewalFeeType),
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'UCC filing fee',
        displayedValue: this.formatFee(offer?.uccFilingFee, offer?.uccFilingFeeType),
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'UCC enforcement/termination fee',
        displayedValue: this.formatFee(offer?.uccEnforcementFee, offer?.uccEnforcementFeeType),
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Servicing fee',
        displayedValue: this.formatFee(offer?.servicingFee, offer?.servicingFeeType),
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Servicing fee minimum',
        displayedValue: this.formatFee(offer?.servicingFeeMinimum, offer?.servicingFeeMinimumType),
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Stacking fee',
        displayedValue: this.formatFee(offer?.stackingFee, offer?.stackingFeeType),
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Late payment fee minimum amount',
        displayedValue: this.formatCurrency(offer?.latePaymentFeeMinimumAmount),
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Late payment fee percentage',
        displayedValue: this.formatPercent(offer?.latePaymentFeePercentage),
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Stop payment fee',
        displayedValue: this.formatFee(offer?.stopPaymentFee, offer?.stopPaymentFeeType),
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Bank account change fee',
        displayedValue: this.formatFee(offer?.bankAccountChangeFee, offer?.bankAccountChangeFeeType),
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Subordination fee',
        displayedValue: this.formatFee(offer?.subordinationFee, offer?.subordinationFeeType),
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Modification fee',
        displayedValue: this.formatFee(offer?.modificationFee, offer?.modificationFeeType),
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Maintenance fee',
        displayedValue: this.formatFee(offer?.maintenanceFee, offer?.maintenanceFeeType),
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Maintenance fee frequency',
        displayedValue: this.enumValueLabelPipe.transform(<any>offer?.maintenanceFeeFrequency, PaymentFrequency, PaymentFrequencyLabel),
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Credit model ID',
        displayedValue: '-'
      }),
    ]
  }

  private formatCurrency(value: number): string {
    return this.currencyPipe.transform(value, "$") || '-';
  }

  private formatPercent(value: number, decimalPlaces: number = 2): string {
    const decimalValue = this.decimalPipe.transform(value, `1.${decimalPlaces}-${decimalPlaces}`);
    return `${decimalValue}%` || '-';
  }

  private formatFee(value: number, type: FeeType,): string {
    switch (type) {
      case FeeType.Flat:
        return this.formatCurrency(value);
      case FeeType.Percentage:
        return value ? this.formatPercent(value) : '';
    }
  }

  private formatTerm(term: number): string {
    return `${term || '-'} ${term === 1 ? 'month' : 'months'}`;
  }

  private formatPaymentTerm(frequency: PaymentFrequency, term: number): string {
    return `${this.getPaymentTermValue(frequency, term) || '-'} ${this.getPaymentTermName(frequency, term)}`;
  }

  private getPaymentTermValue(frequency?: PaymentFrequency, value?: number): number {
    switch (frequency) {
      case PaymentFrequency.BiWeekly:
        return value * 2;

      default:
        return value;
    }
  }

  private getPaymentTermName(frequency?: PaymentFrequency, value?: number): string {
    switch (frequency) {
      case PaymentFrequency.Monthly:
        return this.getPaymentTermValue(frequency, value) < 2 ? 'month' : 'months';

      default:
        return this.getPaymentTermValue(frequency, value) < 2 ? 'week' : 'weeks';
    }
  }
}